import React, { useState, useMemo, useEffect } from 'react'
import injectSheet from 'react-jss'
import classNames from 'classnames'
import Button from 'pobble-components/dist/button'
import posthog from 'posthog-js'
import { useChargebeeScript } from 'pobble-components/dist/subscriptions/utils'
import { useHistory, useLocation } from 'react-router-dom'
import { apiClient } from 'pobble-components/dist/api'
import pobbleHomeGroupedVisual from '../../../../../images/pobble-home-visual-2.jpg'

const currencyMap = {
  AUD: 'A$',
  NZD: 'NZ$',
  USD: '$',
  GBP: '£'
}

const ParentUpgradeButton = injectSheet({
  annualButton: {
    background: 'var(--yellow) !important',
    color: 'var(--gray) !important',
    border: 'none !important',
    display: 'flex',
    justifyContent: 'center',
    minWidth: '200px',
    '&:hover': {
      background: 'var(--yellow-) !important'
    }
  },
  monthlyButton: {
    background: 'transparent !important',
    color: '#00a1e9 !important',
    border: '1px solid #00a1e9 !important',
    display: 'flex',
    justifyContent: 'center',
    minWidth: '200px',
    '&:hover': {
      background: 'transparent !important'
    }
  }
})(({ id, classes, children, annual }) => {
  const { openCheckout } = useChargebeeScript()
  const history = useHistory()
  const location = useLocation()

  return (
    <Button
      minw
      darkBlue
      tall
      action={() => {
        history.replace({
          pathname: location.pathname,
          search: `from=${id}`
        })
        posthog.capture('user clicked fsu parents cta', {
          id,
          plan: annual ? 'annual' : 'monthly'
        })
        openCheckout({
          url: 'v3/me/subscriptions/checkout_new_pobble_home_js',
          params: { annual },
          options: {
            step: step => {
              posthog.capture('checkout step', {
                id,
                step
              })
            },
            close: () => {
              history.replace({
                pathname: location.pathname,
                search: false
              })
            }
          }
        })
      }}
      className={`${
        annual ? classes.annualButton : classes.monthlyButton
      } metrics__fsu_parents_${annual ? 'annual' : 'monthly'}`}>
      {children}
    </Button>
  )
})

export const FSUParents = injectSheet({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    textAlign: 'left',
    border: '1px solid var(--light-gray)',
    borderRadius: '8px',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      alignItems: 'center',
      gap: '32px'
    }
  },
  content: {
    flex: 1,
    padding: '24px'
  },
  visual: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    '@media (min-width: 768px)': {
      flex: '0 0 42%',
      justifyContent: 'flex-end'
    },
    '@media (max-width: 1060px)': {
      display: 'none'
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '8px',
      borderBottomLeftRadius: '0',
      borderTopLeftRadius: '0'
    }
  },
  titleYearly: {
    marginBottom: '0',
    fontSize: '24px'
  },
  pitch: {
    font: 'var(--body-)',
    margin: '4px 0 0'
  },
  options: {
    gap: '24px',
    margin: '32px 0 0',
    textAlign: 'left',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      alignItems: 'stretch'
    }
  },
  option: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 24px',
    border: '1px solid var(--light-gray)',
    borderRadius: '8px'
  },
  optionFeatured: {
    position: 'relative',
    border: '2px solid var(--yellow)',
    background: '#fff',
    marginBottom: '18px',
    '@media (min-width: 768px)': {
      flex: '1.2'
    }
  },
  optionSecondary: {
    '@media (min-width: 768px)': {
      flex: '1'
    }
  },
  badge: {
    position: 'absolute',
    top: '-12px',
    left: '12%',
    transform: 'translateX(-50%)',
    background: 'var(--yellow)',
    color: '#333',
    fontSize: '12px',
    fontWeight: 'bold',
    padding: '4px 12px',
    borderRadius: '12px',
    whiteSpace: 'nowrap'
  },
  optionTitle: {
    fontSize: '15px',
    margin: 0,
    '& em': {
      fontStyle: 'normal',
      fontWeight: 'normal',
      color: 'var(--gray-)'
    }
  },
  optionTitleFeatured: {
    fontSize: '17px'
  },
  optionDesc: {
    font: 'var(--body-)',
    color: 'var(--gray-)',
    margin: '8px 0 0',
    lineHeight: '24px',
    fontSize: '15px'
  },
  optionDescHolder: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  strikethrough: {
    textDecoration: 'line-through'
  }
})(({ classes }) => {
  const id = 'fsu-parents'

  const [subsData, setSubsData] = useState(null)

  const price = useMemo(
    () => {
      if (!subsData) return null
      const currency = currencyMap[subsData.currency]
      const annualAmount = parseFloat(subsData.pobble_home_annual)
      const monthlyAmount = parseFloat(subsData.pobble_home)

      return {
        currency,
        annual: annualAmount.toFixed(2),
        monthly: monthlyAmount.toFixed(2),
        annualPerMonth: (annualAmount / 12).toFixed(0),
        monthlyPerYear: (monthlyAmount * 12).toFixed(2),
        savings: (monthlyAmount * 12 - annualAmount).toFixed(2)
      }
    },
    [subsData]
  )

  useEffect(() => {
    apiClient
      .get('v3/subscriptions/marketing_data')
      .then(res => setSubsData(res.data.subscription_marketing_data))
  }, [])

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <h2 className={classes.titleYearly}>
          The spark your child needs to enjoy writing
        </h2>
        <p className={classes.pitch}>Choose monthly, or save 33% annually.</p>
        <div className={classes.options}>
          <div className={classNames(classes.option, classes.optionFeatured)}>
            <span className={classes.badge}>Save 33%</span>
            <h3
              className={classNames(
                classes.optionTitle,
                classes.optionTitleFeatured
              )}>
              Yearly
            </h3>
            <div className={classes.optionDescHolder}>
              <p className={classes.optionDesc}>
                {price ? (
                  <>
                    <span className={classes.strikethrough}>
                      {price.currency}
                      {price.monthlyPerYear}
                      /year
                    </span>{' '}
                    <strong>
                      {price.currency}
                      {price.annual}
                      /year.
                    </strong>
                    <br />
                    Just {price.currency}
                    {price.annualPerMonth}
                    /month. Save {price.currency}
                    {price.savings}.
                  </>
                ) : (
                  'Billed annually. Cancel anytime.'
                )}
              </p>
              <ParentUpgradeButton id={id} annual>
                Choose annual
              </ParentUpgradeButton>
            </div>
          </div>
          <div className={classNames(classes.option, classes.optionSecondary)}>
            <h3
              className={classNames(
                classes.optionTitle,
                classes.optionTitleFeatured
              )}>
              Monthly
            </h3>
            <div className={classes.optionDescHolder}>
              <p className={classes.optionDesc}>
                {price ? (
                  <>
                    <strong>
                      {price.currency}
                      {price.monthly}
                      /month.
                    </strong>{' '}
                    <br />
                    Renews monthly unless you cancel.
                  </>
                ) : (
                  'First month is £1. Cancel anytime.'
                )}
              </p>
              <ParentUpgradeButton id={id} annual={false}>
                Choose monthly
              </ParentUpgradeButton>
            </div>
          </div>
        </div>
      </div>
      <div className={classes.visual}>
        <img
          src={pobbleHomeGroupedVisual}
          alt="Pobble Home on laptop and mobile"
        />
      </div>
    </div>
  )
})
